import { TinyColor } from '@ctrl/tinycolor';
import { h, Component, Element, Prop, Host, State, Watch } from '@stencil/core';

import { getCSSVariable, isNullUndefined, isNumber } from '../../../services/utils';

@Component({
  tag: 'pk-jersey',
  styleUrl: './scss/pk-jersey.scss',
  shadow: true,
})
export class PkJersey {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkJerseyElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private numberColor: string;
  @State() private iconSize: number = 12;
  @State() private iconSizeSmall: number = 10;
  @State() private fontSize: number = 8;

  // Public Property API
  // ========================

  /** Set the shirt color */
  @Prop({ reflect: true }) shirtColor!: string;

  /** Player jersey number */
  @Prop({ reflect: true }) number!: number;

  /** Set player in/out substitution */
  @Prop({ reflect: true }) substitution: 'in' | 'out';

  /** Set player cards */
  @Prop({ reflect: true }) cards: 'yellow' | 'red' | 'yellow-red';

  /** Set jersey size */
  @Prop() size: number = 32;

  /** Set player scored goals number */
  @Prop() goals: number = 0;

  /** Set player scored own-goals number */
  @Prop() ownGoals: number = 0;

  /** Activate captain label */
  @Prop({ reflect: true }) captainLabel: string;

  // Prop lifecycle events
  // =======================

  @Watch('shirtColor')
  handleNumberColor() {
    if (!this.shirtColor) return;

    const light = new TinyColor(this.shirtColor).isLight();

    this.numberColor = `text-01--${light ? 'light' : 'dark'}`;
  }

  @Watch('size')
  handleSize() {
    this.iconSize = this.size * 0.375;
    this.iconSizeSmall = this.size * 0.3125;

    this.fontSize = this.size * 0.25;
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.handleNumberColor();
    this.handleSize();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private getGoalsArrayLength(goals: number) {
    if (!isNumber(goals)) return 0;

    return goals > 2 ? 1 : goals;
  }

  private printGoalList(goals: number, isOwnGoal?: boolean) {
    if (!goals) return null;

    const array = new Array(this.getGoalsArrayLength(goals)).fill({});

    return (
      <div class="goals__inner-container flex flex-row-reverse items-center">
        {array?.map((_, i) => (
          <pk-icon
            class={{ goal: true, 'own-goal': isOwnGoal }}
            name="football-goal"
            size={this.iconSize}
            key={`goal-${i + 1}`}
          />
        ))}
        {goals > 2 && <span class="goals__number">{goals}</span>}
      </div>
    );
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.shirtColor && { '--pk-jersey--shirt-color': this.shirtColor }),
      '--jersey--number-color': getCSSVariable(this.numberColor),
      '--jersey--number-font-size': `${this.iconSizeSmall}px`,
      '--jersey--icon-size': `${this.iconSize}px`,
      '--jersey--icon-size-small': `${this.iconSizeSmall}px`,
      '--jersey--label-font-size': `${this.fontSize}px`,
    };

    return (
      <Host style={{ ...style }}>
        <pk-icon
          name={!isNullUndefined(this.captainLabel) ? 'jersey-captain-product-icon' : 'jersey-product-icon'}
          color="jersey--shirt-color"
          class="shirt"
          size={this.size}
          part="jersey-icon"
        />
        <span class="number" part="number">
          {this.number}
        </span>
        <div
          class="absolute right-[calc(100%_-_(var(--jersey--icon-size)_*_0.5))] top-0 flex flex-row-reverse items-center"
          part="goals-container"
        >
          {this.printGoalList(this.ownGoals, true)}
          {this.printGoalList(this.goals)}
        </div>
        {!!this.cards && (
          <pk-icon
            class={{
              'card absolute right-[calc(-0.2_*_var(--jersey--icon-size-small))] top-0': true,
              [`card--${this.cards}`]: !!this.cards,
            }}
            name={this.cards === 'yellow-red' ? 'football-card-yellow-to-red-color' : 'football-card-single-card'}
            size={this.cards === 'yellow-red' ? this.iconSizeSmall : this.iconSize}
            part="cards-icon"
          />
        )}
        {!!this.substitution && (
          <pk-icon
            class={{
              'substitution absolute bottom-0 left-0 rounded-[50%] bg-elevation-01': true,
              [`substitution--${this.substitution}`]: !!this.substitution,
            }}
            name={`football-substitution-${this.substitution}`}
            size={this.iconSize}
            part="substitution-icon"
          />
        )}
        {!!this.captainLabel && (
          <span class="captain" part="captain-label">
            {this.captainLabel}
          </span>
        )}
      </Host>
    );
  }
}
